/**
 * useVideoStats — статистика просмотров видео-моментов игр (DC → Юнифанс → Видео).
 * Источник: GET /api/commerce/video-stats?from=&to= (агрегат client_events video_play/watch).
 * Ретро-данных нет — считается с момента выката инструментированного клиента.
 */
import { useEffect, useState } from "react";
import { api } from "../api/client";

export interface VideoStatsSummary {
  views: number; uniqueViewers: number; watchMin: number; avgWatchSec: number; completionRate: number;
}
export interface VideoDelayBucket { key: string; label: string; count: number }
export interface VideoTopItem {
  materialId: string; bookingId: string; quest: string; gameDate: string; gameTime: string; label: string;
  operator: string; duration: string; plays: number; viewers: number; watchMin: number;
  completionRate: number; streamUrl: string; posterUrl: string;
}
export type VideoLibraryItem = VideoTopItem;
export interface VideoTopViewer {
  tg: number; name: string; username: string; avatarUrl: string;
  videos: number; plays: number; watchMin: number; lastWatch: string;
}
export interface VideoByQuest { quest: string; plays: number; watchMin: number }
export interface VideoInventoryQuest { quest: string; moments: number; games: number }
export interface VideoInventory { totalMoments: number; gamesWithVideo: number; byQuest: VideoInventoryQuest[] }
export interface VideoStats {
  summary: VideoStatsSummary;
  delay: { buckets: VideoDelayBucket[]; avgHours: number };
  topVideos: VideoTopItem[];
  library: VideoLibraryItem[];
  topViewers: VideoTopViewer[];
  byQuest: VideoByQuest[];
  inventory: VideoInventory;
  generatedAt: string;
}

export interface UseVideoStatsResult {
  data: VideoStats | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
}

export interface VideoView {
  tg: number; name: string; username: string; avatarUrl: string; ts: string; date: string; time: string;
}

/** Каждый просмотр конкретного видео-момента (drill-down в модалке): кто + дата/время. */
export function useVideoViews(materialId: string | null, from?: string, to?: string): { views: VideoView[]; loading: boolean; error: string | null } {
  const [views, setViews] = useState<VideoView[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!materialId) { setViews([]); return; }
    let live = true;
    setLoading(true);
    setError(null);
    const qs = new URLSearchParams({ materialId });
    if (from) qs.set("from", from);
    if (to) qs.set("to", to);
    api<{ views: VideoView[] }>(`/api/commerce/video-views?${qs.toString()}`)
      .then((d) => { if (live) setViews(d.views ?? []); })
      .catch((e) => { if (live) setError(e instanceof Error ? e.message : "Ошибка загрузки"); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [materialId, from, to]);
  return { views, loading, error };
}

export function useVideoStats(from?: string, to?: string): UseVideoStatsResult {
  const [data, setData] = useState<VideoStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [trigger, setTrigger] = useState(0);

  useEffect(() => {
    let live = true;
    setLoading(true);
    setError(null);
    const qs = new URLSearchParams();
    if (from) qs.set("from", from);
    if (to) qs.set("to", to);
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    api<VideoStats>(`/api/commerce/video-stats${suffix}`)
      .then((d) => { if (live) setData(d); })
      .catch((e) => { if (live) setError(e instanceof Error ? e.message : "Ошибка загрузки"); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [from, to, trigger]);

  return { data, loading, error, reload: () => setTrigger((n) => n + 1) };
}
